<!--
	Ported from analytics-design/src/components/TopicsDashboard.tsx, then rewired to real data -
	see the component's own comment for what changed. "Manage Topics" is hidden (2026-10-06,
	product decision 2026-10-05: not in v1), see the @if (false) block below.

	SETTE redesign (CLIPPING-598): token-by-token restyle, "Reach" -> "Visibility" in all copy.
	Widget 1's "vs Prev" column and Widget 10's drilldown deltas moved from `.startsWith('+'/'-')`
	string-sniffing to real Tone fields - the formatted text now carries U+2212 for negatives,
	and the drilldown Mentions/Visibility deltas were actually always green regardless of sign
	before this pass (a latent bug, not just a token mismatch).
-->
<div class="space-y-4">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="4" [chartCount]="2"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetch()"></app-analytics-error-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- 1. TOP HEADER ROW -->
	<div class="bg-surface px-4 py-3 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-3">
		<div>
			<h1 class="overline-label text-ink">{{ 'ANALYTICS_GLOSSARY.TOPICS' | translate }}</h1>
			<p class="text-ink-subtle text-xs mt-0.5">{{ 'ANALYTICS_TOPICS.SUBTITLE' | translate }}</p>
		</div>
		<!-- Hidden 2026-10-06 (product decision 2026-10-05): not in v1. Button + modal kept, not deleted. -->
		@if (false) {
		<button type="button" (click)="openManageModal()" class="text-blue font-medium text-xs flex items-center gap-1 hover:underline cursor-pointer">
			<span>Manage Topics</span>
			<mat-icon class="!w-3 !h-3 !text-xs">open_in_new</mat-icon>
		</button>
		}
	</div>

	<!-- 2. KPI CARDS -->
	<div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
		<app-kpi-tile [label]="'ANALYTICS_TOPICS.TOPICS_TRACKED_LABEL' | translate" infoKey="ANALYTICS_INFO.TOPICS.TOPICS_TRACKED" icon="chat" iconColorClass="text-blue" [value]="fmtInt(topicsTrackedCount)" [deltaLabel]="topTopicsFooter"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX' | translate" infoKey="ANALYTICS_INFO.COMMON.VISIBILITY" infoExtraKey="ANALYTICS_INFO.TOPICS.ONLY_TAGGED_NOTE" icon="visibility" iconColorClass="text-ink-subtle" [value]="avgReachValue" [deltaLabel]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate" [sampleSize]="data?.avgReach?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE' | translate" infoKey="ANALYTICS_INFO.COMMON.IMPACT" infoExtraKey="ANALYTICS_INFO.TOPICS.ONLY_TAGGED_NOTE" icon="bolt" iconColorClass="text-ink-subtle" [value]="avgImpactValue" [deltaLabel]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate" [sampleSize]="data?.avgImpact?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.NET_SENTIMENT' | translate" infoKey="ANALYTICS_INFO.COMMON.NET_SENTIMENT" infoExtraKey="ANALYTICS_INFO.TOPICS.ONLY_TAGGED_NOTE" icon="auto_awesome" iconColorClass="text-ink-subtle" [value]="netSentimentValue" [deltaLabel]="'ANALYTICS_TOPICS.NET_SENTIMENT_SUBLABEL' | translate" [sampleSize]="data?.netSentiment?.sampleSize"></app-kpi-tile>
	</div>

	<!-- 3. MAIN WIDGETS GRID -->
	<div class="space-y-4">
		<!-- ROW 1 (3 equal columns): Widgets 1, 2, 3 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 1: Top Topics by Mentions -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">1. {{ 'ANALYTICS_TOPICS.TOP_TOPICS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOP_TOPICS_BY_MENTIONS"></app-info-icon></h3>
					<div class="grid grid-cols-12 gap-2 items-center">
						<div class="col-span-8 relative w-full min-h-[250px] h-full">
							<app-chart type="bar" [data]="topTopicsChartData" [options]="topTopicsChartOptions" height="100%"></app-chart>
						</div>
						<div class="col-span-4 pl-2 border-l border-line space-y-2.5">
							<div class="overline-label text-ink-subtle border-b border-line pb-1 flex justify-between">
								<span>{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</span>
								<span>{{ 'ANALYTICS_TOPICS.VS_PREV' | translate }}</span>
							</div>
							@for (topic of topTopicsData; track topic.name) {
							<div class="flex items-center justify-between text-[11px]">
								<span class="font-medium text-ink tabular-nums">{{ fmtInt(topic.mentions) }}</span>
								<span class="caption tabular-nums" [class]="toneTextClass(topic.change.tone)">{{ topic.change.text }}</span>
							</div>
							}
						</div>
					</div>
				</div>
				<div class="flex items-center justify-between caption text-ink-subtle font-normal mt-2 pt-2 border-t border-line">
					<span>{{ topTopicsFooter }}</span>
					@if (topicsTotalMentions > 0) {
					<span class="caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
					}
				</div>
			</div>

			<!-- WIDGET 2: Topic Trend (Mentions) -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<div class="flex items-center justify-between mb-1">
						<h3 class="section-title flex items-center gap-1">2. {{ 'ANALYTICS_TOPICS.TOPIC_TREND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOPIC_TREND"></app-info-icon></h3>
						<select [(ngModel)]="timeTrendInterval" class="bg-surface-gray border border-line-strong text-ink text-[11px] rounded-full px-2.5 h-7 focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 font-medium">
							<option value="Daily">{{ 'ANALYTICS_TOPICS.INTERVAL_DAILY' | translate }}</option>
							<option value="Weekly">{{ 'ANALYTICS_TOPICS.INTERVAL_WEEKLY' | translate }}</option>
							<option value="Monthly">{{ 'ANALYTICS_TOPICS.INTERVAL_MONTHLY' | translate }}</option>
						</select>
					</div>
					<div class="flex flex-wrap items-center gap-2 mb-1.5 caption">
						@for (l of topicTrendLegend; track l.name) {
						<span class="flex items-center gap-1 font-normal text-ink-muted"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="l.color"></span> {{ l.name }}</span>
						}
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="line" [data]="topicTrendChartData" [options]="topicTrendChartOptions" height="100%"></app-chart>
				</div>
				@if (topicsTotalMentions > 0) {
				<span class="block mt-1 caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
				}
			</div>

			<!-- WIDGET 3: Topic Share of Voice -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<h3 class="section-title mb-1 flex items-center gap-1">3. {{ 'ANALYTICS_TOPICS.TOPIC_SOV_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOPIC_SOV"></app-info-icon></h3>
				<div class="relative w-full min-h-[240px] h-full flex-1">
					<app-chart type="bar" [data]="topicSovChartData" [options]="topicSovChartOptions" height="100%"></app-chart>
				</div>
				@if (topicsTotalMentions > 0) {
				<span class="block caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
				}
				<p class="caption text-ink-subtle font-normal italic mt-2">{{ 'ANALYTICS_TOPICS.TOPIC_SOV_FOOTNOTE' | translate }}</p>
			</div>
		</div>

		<!-- ROW 2 (3 equal columns): Widgets 4, 5, 6 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 4: Topic Group Sentiment -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">4. {{ 'ANALYTICS_TOPICS.TOPIC_GROUP_SENTIMENT_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOPIC_GROUP_SENTIMENT"></app-info-icon></h3>
					<div class="flex items-center gap-3 mb-1.5 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span>
					</div>
				</div>
				<div class="relative w-full min-h-[240px] h-full flex-1 flex flex-col justify-center">
					<app-chart type="bar" [data]="topicGroupChartData" [options]="stackedPercentChartOptions" height="100%"></app-chart>
				</div>
				@if (topicsTotalMentions > 0) {
				<span class="block mt-1 caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
				}
			</div>

			<!-- WIDGET 5: Mentions by Topic and Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">5. {{ 'ANALYTICS_TOPICS.MENTIONS_BY_TOPIC_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.MENTIONS_BY_TOPIC_AND_BRAND"></app-info-icon></h3>
					<div class="flex flex-wrap items-center gap-2 mb-1.5 caption">
						@for (col of topicBrandColumns; track col.key) {
						<span class="flex items-center gap-1 font-normal text-ink-muted"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="col.color"></span> {{ col.label }}</span>
						}
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="topicBrandChartData" [options]="stackedPercentChartOptions" height="100%"></app-chart>
				</div>
				<div class="caption text-ink-subtle font-normal mt-2 pt-1 border-t border-line flex items-center justify-between">
					<span>{{ topicBrandFooter }}</span>
					@if (topicsTotalMentions > 0) {
					<span class="caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
					}
				</div>
			</div>

			<!-- WIDGET 6: Topic Sentiment Overview -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">6. {{ 'ANALYTICS_TOPICS.TOPIC_SENTIMENT_OVERVIEW_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOPIC_SENTIMENT_OVERVIEW"></app-info-icon></h3>
					<div class="flex items-center gap-3 mb-1.5 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span>
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="topicSentimentChartData" [options]="stackedPercentChartOptions" height="100%"></app-chart>
				</div>
				@if (topicsTotalMentions > 0) {
				<span class="block mt-1 caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
				}
			</div>
		</div>

		<!-- ROW 3 (4 columns): Widgets 7, 8, 9, 10 -->
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
			<!-- WIDGET 7: Avg Visibility Index by Topic -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<h3 class="section-title mb-2 flex items-center gap-1">7. {{ 'ANALYTICS_TOPICS.AVG_VISIBILITY_BY_TOPIC_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.AVG_VISIBILITY_BY_TOPIC"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="reachTopicChartData" [options]="reachTopicChartOptions" height="100%"></app-chart>
				</div>
				@if (topicsTotalMentions > 0) {
				<span class="block mt-1 caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
				}
			</div>

			<!-- WIDGET 8: Avg Impact Score by Topic -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<h3 class="section-title mb-2 flex items-center gap-1">8. {{ 'ANALYTICS_TOPICS.AVG_IMPACT_BY_TOPIC_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.AVG_IMPACT_BY_TOPIC"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="impactTopicChartData" [options]="impactTopicChartOptions" height="100%"></app-chart>
				</div>
				@if (topicsTotalMentions > 0) {
				<span class="block mt-1 caption text-ink-subtle font-normal">{{ topicsSampleNote }}</span>
				}
			</div>

			<!-- WIDGET 9: Top Negative Topics -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<div class="flex items-center justify-between mb-2">
						<h3 class="section-title flex items-center gap-1">9. {{ 'ANALYTICS_TOPICS.TOP_NEGATIVE_TOPICS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOP_NEGATIVE_TOPICS"></app-info-icon></h3>
						<span class="caption bg-negative-tint text-negative px-2 py-0.5 rounded-full font-medium">{{ 'ANALYTICS_TOPICS.RISK_AREAS_BADGE' | translate }}</span>
					</div>
					<div class="overflow-x-auto">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-1.5">{{ 'ANALYTICS_GLOSSARY.TOPIC' | translate }}</th>
									<th class="pb-1.5 text-right">{{ 'ANALYTICS_TOPICS.NEG_PERCENT_HEADER' | translate }}</th>
									<th class="pb-1.5 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (item of topNegativeTopics; track item.topic) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-2"><span class="font-medium text-ink block truncate max-w-[130px]">{{ item.topic }}</span></td>
									<td class="py-2 text-right"><span class="font-medium text-negative bg-negative-tint px-1.5 py-0.2 rounded-full text-[10px] tabular-nums">{{ item.negativePct }}</span></td>
									<td class="py-2 text-right font-medium text-ink tabular-nums">{{ item.mentions }}</td>
								</tr>
								}
								@if (!loading && topNegativeTopics.length === 0) {
								<tr><td colspan="3" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_TOPICS.NO_SENTIMENT_DATA' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- WIDGET 10: Topic Drilldown -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<div class="flex items-center justify-between mb-2">
						<h3 class="section-title flex items-center gap-1">10. {{ 'ANALYTICS_TOPICS.TOPIC_DRILLDOWN_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.TOPICS.TOPIC_DRILLDOWN"></app-info-icon></h3>
						<select
							[(ngModel)]="selectedDrilldownTopic"
							class="bg-blue-tint border border-blue text-blue text-[10px] font-medium rounded-full px-2.5 h-7 focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 cursor-pointer">
							@for (t of drilldownTopics; track t.raw) {
							<option [value]="t.raw">{{ t.label }}</option>
							}
						</select>
					</div>

					<div class="grid grid-cols-3 gap-1.5 p-2 rounded-xl bg-surface-gray mb-3">
						<div>
							<span class="overline-label text-ink-subtle block">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</span>
							<span class="text-xs font-medium text-ink block tabular-nums">{{ drilldownMentions }}</span>
							<span class="caption font-medium tabular-nums" [class]="toneTextClass(drilldownMentionsChg.tone)">{{ drilldownMentionsChg.text }}</span>
						</div>
						<div>
							<span class="overline-label text-ink-subtle block">{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}</span>
							<span class="text-xs font-medium text-ink block tabular-nums">{{ drilldownReach }}</span>
							<span class="caption font-medium tabular-nums" [class]="toneTextClass(drilldownReachChg.tone)">{{ drilldownReachChg.text }}</span>
						</div>
						<div>
							<span class="overline-label text-ink-subtle block">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</span>
							<span class="text-xs font-medium block tabular-nums" [class]="toneTextClass(drilldownImpactTone)">{{ drilldownImpact }}</span>
							<span class="caption font-medium tabular-nums" [class]="toneTextClass(drilldownImpactChg.tone)">{{ drilldownImpactChg.text }}</span>
						</div>
					</div>

					<div class="flex items-center gap-3 p-3 rounded-xl bg-surface-gray">
						<div class="relative w-16 h-16 shrink-0">
							<app-chart type="doughnut" [data]="drilldownSentimentChartData" [options]="drilldownSentimentChartOptions" height="100%"></app-chart>
						</div>
						<div class="caption space-y-1 flex-1">
							<div class="flex items-center justify-between text-positive font-medium"><span>{{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span><span>{{ drilldownSentiment.pos }}%</span></div>
							<div class="flex items-center justify-between text-ink-subtle font-normal"><span>{{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span><span>{{ drilldownSentiment.neu }}%</span></div>
							<div class="flex items-center justify-between text-negative font-medium"><span>{{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span><span>{{ drilldownSentiment.neg }}%</span></div>
						</div>
					</div>
					@if (currentDrilldownRaw?.mentions) {
					<app-sample-size-note class="block mt-1" [count]="currentDrilldownRaw!.mentions!"></app-sample-size-note>
					}
				</div>

				<div class="flex items-center caption text-ink-subtle font-normal mt-2 pt-2 border-t border-line">
					<span class="truncate">{{ 'ANALYTICS_TOPICS.ACTIVE_TOPIC_FILTER' | translate }}: {{ selectedDrilldownTopicLabel || '—' }}</span>
				</div>
			</div>
		</div>
	</div>

	<!-- 4. FOOTER NOTE -->
	<div class="text-center py-2.5 px-4 bg-surface-gray rounded-2xl border border-line text-ink-subtle caption font-normal">
		<span>{{ 'ANALYTICS_TOPICS.FOOTER_NOTE' | translate }}</span>
	</div>
	</div>
	}

	<!-- MANAGE TOPICS MODAL - hidden 2026-10-06 (product decision 2026-10-05): not in v1 -->
	@if (false && showManageModal) {
	<div class="fixed inset-0 bg-ink/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl border border-line shadow-pop max-w-lg w-full p-5 space-y-4">
			<div class="flex items-center justify-between border-b border-line pb-3">
				<div class="flex items-center gap-2">
					<mat-icon class="!w-4 !h-4 !text-base text-blue">tag</mat-icon>
					<h3 class="item-title text-ink">Topics with coverage this period</h3>
				</div>
				<button type="button" (click)="closeManageModal()" class="text-ink-subtle hover:text-ink p-1 rounded-lg hover:bg-surface-gray cursor-pointer">
					<mat-icon class="!w-4 !h-4 !text-base">close</mat-icon>
				</button>
			</div>

			<p class="text-ink-subtle text-xs">Topic keyword/category configuration lives in Settings, not here.</p>

			<div class="space-y-2 max-h-64 overflow-y-auto pr-1">
				@for (t of allTopics; track t.name) {
				<div class="flex items-center justify-between p-2.5 rounded-xl border border-line bg-surface-gray">
					<div class="font-medium text-ink">{{ t.name }}</div>
					<span class="text-[10px] text-ink-subtle font-medium">{{ fmtInt(t.mentions) }} mentions</span>
				</div>
				}
			</div>

			<div class="flex items-center justify-between pt-3 border-t border-line">
				<button type="button" (click)="addCustomTopic()" class="flex items-center gap-1 text-blue font-medium text-xs hover:underline cursor-pointer">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm">add</mat-icon>
					<span>Add Custom Topic</span>
				</button>
				<button type="button" (click)="closeManageModal()" class="h-10 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full text-xs transition-colors cursor-pointer">Done</button>
			</div>
		</div>
	</div>
	}
</div>
